import React from "react";
import {
    Box,
    FormControl,
    ListItemText,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Langs from "../../app/lang/langs";

/**
 * Evaluation type picker for the Évaluations header.
 *
 * Replaced a three-button SelectButton hardcoded to Collab / DM / Référent. Types are
 * data now — an admin can create one in Settings — so the picker has to be driven by the
 * list, or a new type is unreachable no matter how it is configured.
 *
 * Presentational: the parent owns `value` and reacts to `onChange`.
 *
 * @param {Array}    types     [{id, title, rank}] — already scoped to the selected teams
 * @param {number}   value     currently selected type id
 * @param {Function} onChange  callback(idType)
 * @param {boolean}  fullWidth stretch to the container (mobile)
 */

const ACCENT = "rgb(255,144,21)";
const ACCENT_SOFT = "rgba(255,144,21,";
const TRANSITION = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

export default function EvaluationTypeSelector({
    types = [],
    value,
    onChange,
    fullWidth = false,
    disabled = false,
}) {

    // Guard against a value that isn't in the list (team switch can drop a scoped type):
    // MUI logs a warning and renders blank for an out-of-range value.
    const options = types.filter(t => t && t.id != null);
    const currentId = options.some(t => String(t.id) === String(value)) ? value : '';

    return (
        <FormControl
            size="small"
            fullWidth={fullWidth}
            disabled={disabled || !options.length}
            sx={{ minWidth: fullWidth ? undefined : 260 }}
        >
            <Select
                value={currentId}
                onChange={(e) => onChange(e.target.value)}
                IconComponent={ExpandMoreRoundedIcon}
                displayEmpty
                renderValue={(selected) => {
                    const type = options.find(t => String(t.id) === String(selected));
                    return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                            <ChecklistRoundedIcon
                                sx={{ fontSize: "1.1rem", color: ACCENT, flexShrink: 0 }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    component="span"
                                    sx={{
                                        display: "block",
                                        fontSize: "0.68rem",
                                        fontWeight: 600,
                                        lineHeight: 1.1,
                                        letterSpacing: "0.04em",
                                        textTransform: "uppercase",
                                        color: "rgba(0,0,0,0.42)",
                                    }}
                                >
                                    <Langs str="EVAL_TYPE_SELECTOR_LABEL" />
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{
                                        display: "block",
                                        fontSize: "0.9rem",
                                        fontWeight: 700,
                                        lineHeight: 1.25,
                                        letterSpacing: "-0.01em",
                                        color: "rgba(0,0,0,0.82)",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {type ? type.title : <Langs str="EVAL_TYPE_SELECTOR_EMPTY" />}
                                </Typography>
                            </Box>
                        </Box>
                    );
                }}
                MenuProps={{
                    PaperProps: {
                        elevation: 0,
                        sx: {
                            mt: 0.75,
                            borderRadius: "14px",
                            border: "1px solid rgba(0,40,128,0.08)",
                            boxShadow: "0 8px 28px rgba(0,40,128,0.10), 0 2px 6px rgba(0,0,0,0.04)",
                            overflow: "hidden",
                            "& .MuiMenuItem-root": {
                                py: 1,
                                px: 1.75,
                                fontSize: "0.88rem",
                                transition: TRANSITION,
                                "&:hover": { backgroundColor: `${ACCENT_SOFT}0.07)` },
                                "&.Mui-selected": {
                                    backgroundColor: `${ACCENT_SOFT}0.12)`,
                                    fontWeight: 700,
                                    // The accent bar is the only selected-state cue that
                                    // survives the hover background, so keep both.
                                    boxShadow: `inset 3px 0 0 ${ACCENT}`,
                                    "&:hover": { backgroundColor: `${ACCENT_SOFT}0.16)` },
                                },
                            },
                        },
                    },
                }}
                sx={{
                    borderRadius: "12px",
                    backgroundColor: "rgba(255,255,255,0.9)",
                    transition: TRANSITION,
                    "& .MuiSelect-select": {
                        py: 0.75,
                        pl: 1.5,
                        display: "flex",
                        alignItems: "center",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: `${ACCENT_SOFT}0.28)`,
                        transition: TRANSITION,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: `${ACCENT_SOFT}0.5)`,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: ACCENT,
                        borderWidth: "1.5px",
                    },
                    "&:hover": {
                        boxShadow: `0 2px 12px ${ACCENT_SOFT}0.14)`,
                    },
                    "& .MuiSelect-icon": {
                        color: `${ACCENT_SOFT}0.75)`,
                        transition: TRANSITION,
                    },
                }}
            >
                {options.map(type => (
                    <MenuItem key={type.id} value={type.id}>
                        <ListItemText
                            primary={type.title}
                            primaryTypographyProps={{ fontSize: "0.88rem" }}
                        />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
